import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  FileDownload,
  Map,
  Refresh,
} from '@mui/icons-material';
import MapRadiusPicker from '../components/MapRadiusPicker';
import { eventWallClockInput, eventWallClockPayload, formatEventSchedule } from '../utils/hackathonFlow';
import { parsePresenceRadius, radiusErrorMessage } from '../utils/presenceRadius';
import { PageHeader, useFeedback } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import { EventContext } from '../contexts/EventContext';
import api from '../config/axios';
import { readSessionUser } from '../utils/sessionUser';
import './Events.css';

const emptyEvent = {
  nome: '',
  descricao: '',
  start_datetime: '',
  end_datetime: '',
  latitude: null,
  longitude: null,
  radius_m: 10,
  endereco: '',
  public_presence: false,
  organization_id: '',
};

const emptySubEvent = {
  nome: '',
  descricao: '',
  start_datetime: '',
  end_datetime: '',
  latitude: null,
  longitude: null,
  radius_m: 10,
  participation_limit: '',
  endereco: '',
};

const formatDateTimeInput = (value) => eventWallClockInput(value);

const EventsPage = () => {
  const {
    events,
    subEventsByEvent,
    loading,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    fetchSubEvents,
    createSubEvent,
    updateSubEvent,
    deleteSubEvent,
    downloadReport,
  } = useContext(EventContext);
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const sessionUser = readSessionUser();
  const [organizations, setOrganizations] = useState([]);

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [subEventDialogOpen, setSubEventDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, message: '' });
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [subEventForm, setSubEventForm] = useState(emptySubEvent);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingSubEvent, setEditingSubEvent] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const selectedSubEvents = useMemo(
    () => (selectedEventId ? subEventsByEvent[selectedEventId] || [] : []),
    [selectedEventId, subEventsByEvent]
  );

  useEffect(() => {
    fetchEvents().catch((error) => notify(error.message || 'Não foi possível carregar os eventos.', 'error'));
  }, [fetchEvents, notify]);

  useEffect(() => {
    api.get('/organizations').then((response) => setOrganizations(response.data || [])).catch(() => setOrganizations([]));
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchSubEvents(selectedEventId);
    }
  }, [fetchSubEvents, selectedEventId]);

  const handleOpenEventDialog = (event) => {
    setEditingEvent(event || null);
    setEventForm(
      event
        ? {
            nome: event.nome,
            descricao: event.descricao || '',
            start_datetime: formatDateTimeInput(event.start_datetime),
            end_datetime: formatDateTimeInput(event.end_datetime),
            latitude: event.latitude,
            longitude: event.longitude,
            radius_m: event.radius_m || 100,
            endereco: event.endereco || '',
            public_presence: Boolean(event.public_presence),
            organization_id: event.organization_id ? String(event.organization_id) : '',
          }
        : { ...emptyEvent }
    );
    setEventDialogOpen(true);
  };

  const handleOpenSubEventDialog = (subEvent, baseEvent) => {
    const reference = subEvent || baseEvent;
    setEditingSubEvent(subEvent || null);
    setSubEventForm(
      reference
        ? {
            nome: subEvent?.nome || '',
            descricao: subEvent?.descricao || '',
            start_datetime: formatDateTimeInput(subEvent?.start_datetime || ''),
            end_datetime: formatDateTimeInput(subEvent?.end_datetime || ''),
            latitude: subEvent?.latitude ?? baseEvent?.latitude ?? null,
            longitude: subEvent?.longitude ?? baseEvent?.longitude ?? null,
            radius_m: subEvent?.radius_m ?? baseEvent?.radius_m ?? 100,
            participation_limit: subEvent?.participation_limit ?? '',
            endereco: subEvent?.endereco || baseEvent?.endereco || '',
          }
        : { ...emptySubEvent }
    );
    setSubEventDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setEventDialogOpen(false);
    setSubEventDialogOpen(false);
    setEditingEvent(null);
    setEditingSubEvent(null);
    setEventForm({ ...emptyEvent });
    setSubEventForm({ ...emptySubEvent });
  };

  const handleConfirm = (action, message) => {
    setConfirmDialog({ open: true, action, message });
  };

  const executeConfirmAction = async () => {
    const action = confirmDialog.action;
    setConfirmDialog({ open: false, action: null, message: '' });
    if (action) {
      await action();
    }
  };

  const handleEventSubmit = async () => {
    const payload = {
      nome: eventForm.nome,
      descricao: eventForm.descricao,
      start_datetime: eventWallClockPayload(eventForm.start_datetime),
      end_datetime: eventWallClockPayload(eventForm.end_datetime),
      latitude: eventForm.latitude,
      longitude: eventForm.longitude,
      radius_m: eventForm.radius_m,
      endereco: eventForm.endereco,
      public_presence: Boolean(eventForm.public_presence),
      organization_id: eventForm.organization_id || null,
    };

    try {
      if (!eventForm.nome?.trim()) {
        notify('O nome do evento é obrigatório.', 'warning');
        return;
      }
      const radius = parsePresenceRadius(eventForm.radius_m);
      if (!radius.ok) {
        notify(radiusErrorMessage, 'warning');
        return;
      }
      payload.radius_m = radius.value;
      if (editingEvent) {
        await updateEvent(editingEvent.id, payload);
        notify('Evento atualizado.', 'success');
      } else {
        const created = await createEvent(payload);
        setSelectedEventId((prev) => prev || created?.id || null);
        notify('Evento criado.', 'success');
      }
      handleCloseDialogs();
    } catch (error) {
      notify(error.message || 'Não foi possível salvar o evento.', 'error');
    }
  };

  const handleSubEventSubmit = async () => {
    if (!selectedEventId && !editingSubEvent) {
      return;
    }

    const payload = {
      nome: subEventForm.nome,
      descricao: subEventForm.descricao,
      start_datetime: eventWallClockPayload(subEventForm.start_datetime),
      end_datetime: eventWallClockPayload(subEventForm.end_datetime),
      latitude: subEventForm.latitude,
      longitude: subEventForm.longitude,
      radius_m: subEventForm.radius_m,
      participation_limit:
        subEventForm.participation_limit === '' || subEventForm.participation_limit == null
          ? null
          : Number(subEventForm.participation_limit),
      endereco: subEventForm.endereco,
    };
    const radius = parsePresenceRadius(subEventForm.radius_m);
    if (!radius.ok) {
      notify(radiusErrorMessage, 'warning');
      return;
    }
    payload.radius_m = radius.value;

    if (editingSubEvent) {
      await updateSubEvent(editingSubEvent.id, payload);
    } else {
      await createSubEvent(selectedEventId, payload);
    }

    handleCloseDialogs();
  };

  const handleDeleteEvent = (event) => {
    handleConfirm(async () => {
      await deleteEvent(event.id);
      if (selectedEventId === event.id) {
        setSelectedEventId(null);
      }
    }, `Deseja remover o evento "${event.nome}" e todos seus subeventos?`);
  };

  const handleDeleteSubEvent = (subEvent) => {
    handleConfirm(async () => {
      await deleteSubEvent(selectedEventId, subEvent.id);
    }, `Confirma remover o subevento "${subEvent.nome}"?`);
  };

  const handleDownload = async ({ eventId, subEventId, format }) => {
    try {
      setDownloadingReport(true);
      const response = await downloadReport({ eventId, subEventId, format });
      if (!response) return;

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = subEventId ? `-sub-${subEventId}` : '';
      link.setAttribute('download', `relatorio-evento-${eventId}${suffix}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notify(error?.response?.data?.message || 'Não foi possível baixar o relatório.', 'error');
    } finally {
      setDownloadingReport(false);
    }
  };

  const renderEventCards = () => {
    if (loading) {
      return (
        <Box className="events-loading">
          <CircularProgress color="primary" />
          <Typography variant="body2">Carregando eventos...</Typography>
        </Box>
      );
    }

    if (!events.length) {
      return (
        <Box className="events-empty">
          <Typography variant="h6">Nenhum evento cadastrado ainda.</Typography>
          <Typography variant="body2" color="rgba(245,247,250,0.7)">
            Crie um novo evento para começar a monitorar os participantes.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {events.map((event) => {
          const isSelected = event.id === selectedEventId;
          return (
            <Grid item xs={12} md={6} lg={4} key={event.id}>
              <Card
                className={`event-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedEventId(event.id)}
              >
                <CardContent>
                  <Box className="event-card-header">
                    <Typography variant="h6" fontWeight={700} className="ellipsis">
                      {event.nome}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Editar evento">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEventDialog(event); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover evento">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <Typography variant="body2" className="event-datetime" gutterBottom>
                    {event.start_datetime
                      ? `${formatEventSchedule(event.start_datetime)} - ${
                          event.end_datetime ? formatEventSchedule(event.end_datetime) : 'sem data fim'
                        }`
                      : 'Data a definir'}
                  </Typography>
                  <Typography variant="body2" color="rgba(245,247,250,0.75)" className="ellipsis">
                    {event.descricao || 'Sem descrição informada.'}
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: 'rgba(68,224,198,0.2)' }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Subeventos: ${event.total_sub_events || 0}`} size="small" />
                    <Chip label={`Participantes: ${event.total_participantes || 0}`} size="small" />
                    {event.radius_m ? <Chip label={`Raio: ${event.radius_m}m`} size="small" icon={<Map fontSize="small" />} /> : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderSubEventList = () => {
    if (!selectedEventId) {
      return (
        <Box className="subevents-empty">
          <Typography variant="subtitle1">Selecione um evento para visualizar os subeventos.</Typography>
        </Box>
      );
    }

    if (!selectedSubEvents.length) {
      return (
        <Box className="subevents-empty">
          <Typography variant="subtitle1">Nenhum subevento cadastrado.</Typography>
          <Typography variant="body2" color="rgba(245,247,250,0.7)">
            Use o botão "Adicionar subevento" para criar sessões específicas deste evento.
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} className="subevents-list">
        {selectedSubEvents.map((subEvent) => (
          <Box key={subEvent.id} className="subevent-card">
            <Box className="subevent-header">
              <Typography variant="subtitle1" fontWeight={600} className="ellipsis">
                {subEvent.nome}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Editar subevento">
                  <IconButton size="small" onClick={() => handleOpenSubEventDialog(subEvent, events.find((e) => e.id === selectedEventId))}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover subevento">
                  <IconButton size="small" color="error" onClick={() => handleDeleteSubEvent(subEvent)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Baixar relatório deste subevento (CSV)">
                  <IconButton
                    size="small"
                    onClick={() => handleDownload({ eventId: selectedEventId, subEventId: subEvent.id, format: 'csv' })}
                  >
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Typography variant="body2" color="rgba(245,247,250,0.75)">
              {subEvent.descricao || 'Sem descrição.'}
            </Typography>
            <Box className="subevent-meta">
              <Chip
                label={subEvent.start_datetime ? formatEventSchedule(subEvent.start_datetime) : 'Data a definir'}
                size="small"
                color="primary"
              />
              <Chip
                label={`Participantes: ${subEvent.total_participantes || 0}`}
                size="small"
              />
              {subEvent.participation_limit ? (
                <Chip
                  label={`Limite: ${subEvent.participation_limit}`}
                  size="small"
                  color={Number(subEvent.total_participantes || 0) >= Number(subEvent.participation_limit) ? 'warning' : 'default'}
                />
              ) : (
                <Chip label="Sem limite" size="small" />
              )}
              {subEvent.radius_m ? <Chip label={`Raio ${subEvent.radius_m}m`} size="small" /> : null}
            </Box>
          </Box>
        ))}
      </Stack>
    );
  };

  const selectedEvent = selectedEventId ? events.find((event) => event.id === selectedEventId) : null;

  return (
    <Box className="events-page">
      <Box className="events-content">
        <PageHeader
          title="Eventos"
          subtitle="Estrutura e configuração do evento. Os inscritos ficam em Participantes."
          actions={(
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => fetchEvents()}
              className="ghost-button"
            >
              Atualizar lista
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenEventDialog(null)} className="primary-button">
              Novo evento
            </Button>
          </Stack>
          )}
        />

        <Box className="events-grid">
          <Box className="events-column">{renderEventCards()}</Box>

          <Box className="events-column detail">
            <Box className="detail-header">
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {selectedEvent ? selectedEvent.nome : 'Subeventos'}
                </Typography>
                {selectedEvent ? (
                  <Typography variant="body2" color="rgba(245,247,250,0.7)">
                    Gerencie as sessões e acompanhe a lotação em tempo real.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="rgba(245,247,250,0.7)">
                    Escolha um evento para visualizar os subeventos.
                  </Typography>
                )}
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Tooltip title="Relatório completo do evento (CSV)">
                  <span>
                    <Button
                      startIcon={<FileDownload />}
                      disabled={!selectedEvent || downloadingReport}
                      onClick={() => selectedEvent && handleDownload({ eventId: selectedEvent.id, format: 'csv' })}
                      className="ghost-button"
                    >
                      Exportar CSV
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Relatório completo do evento (PDF)">
                  <span>
                    <Button
                      startIcon={<FileDownload />}
                      disabled={!selectedEvent || downloadingReport}
                      onClick={() => selectedEvent && handleDownload({ eventId: selectedEvent.id, format: 'pdf' })}
                      className="ghost-button"
                    >
                      Exportar PDF
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  className="ghost-button"
                  disabled={!selectedEvent}
                  onClick={() => selectedEvent && navigate(`/presenca?evento=${selectedEvent.id}`)}
                >
                  Presença
                </Button>
                <Button
                  variant="outlined"
                  className="ghost-button"
                  disabled={!selectedEvent}
                  onClick={() => selectedEvent && navigate(`/participantes?evento=${selectedEvent.id}`)}
                >
                  Participantes
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  className="primary-button"
                  disabled={!selectedEvent}
                  onClick={() => selectedEvent && handleOpenSubEventDialog(null, selectedEvent)}
                >
                  Adicionar subevento
                </Button>
              </Stack>
            </Box>
            <Divider sx={{ mb: 3, borderColor: 'rgba(68,224,198,0.12)' }} />
            {renderSubEventList()}
          </Box>
        </Box>
      </Box>

      {/* Modal de evento */}
      <Dialog
        open={eventDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'dialog-surface', sx: { overflowX: 'hidden', maxWidth: '100%' } }}
      >
        <DialogTitle>{editingEvent ? 'Editar evento' : 'Criar novo evento'}</DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'hidden', minWidth: 0 }}>
          <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <TextField
              label="Nome do evento"
              fullWidth
              required
              value={eventForm.nome}
              onChange={(e) => setEventForm((prev) => ({ ...prev, nome: e.target.value }))}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              minRows={3}
              value={eventForm.descricao}
              onChange={(e) => setEventForm((prev) => ({ ...prev, descricao: e.target.value }))}
            />
            <Grid container spacing={2} sx={{ width: '100%', minWidth: 0 }}>
              <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TextField
                  label="Início"
                  type="datetime-local"
                  fullWidth
                  sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0, maxWidth: '100%' } }}
                  value={eventForm.start_datetime}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, start_datetime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TextField
                  label="Término"
                  type="datetime-local"
                  fullWidth
                  sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0, maxWidth: '100%' } }}
                  value={eventForm.end_datetime}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, end_datetime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Endereço ou referência"
              fullWidth
              value={eventForm.endereco}
              onChange={(e) => setEventForm((prev) => ({ ...prev, endereco: e.target.value }))}
            />
            <TextField
              select
              label="Organização responsável"
              fullWidth
              value={eventForm.organization_id || ''}
              onChange={(event) => setEventForm((prev) => ({ ...prev, organization_id: event.target.value }))}
              disabled={sessionUser?.tipo === 'gerente'}
            >
              <MenuItem value="">Selecione</MenuItem>
              {organizations.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.nome}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={Boolean(eventForm.public_presence)}
                onChange={(event) => setEventForm((prev) => ({ ...prev, public_presence: event.target.checked }))}
                inputProps={{ 'aria-label': 'Permitir presença pública no hackathon' }}
              />
              <Typography variant="body2">Permitir presença pública no hackathon</Typography>
            </Box>
            <MapRadiusPicker
              value={{
                latitude: eventForm.latitude,
                longitude: eventForm.longitude,
                radius_m: eventForm.radius_m,
                endereco: eventForm.endereco,
              }}
              onChange={(value) => setEventForm((prev) => ({ ...prev, ...value }))}
              mapHeight={340}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialogs} className="ghost-button">
            Cancelar
          </Button>
          <Button onClick={handleEventSubmit} variant="contained" className="primary-button">
            {editingEvent ? 'Salvar alterações' : 'Criar evento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de subevento */}
      <Dialog
        open={subEventDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'dialog-surface', sx: { overflowX: 'hidden', maxWidth: '100%' } }}
      >
        <DialogTitle>{editingSubEvent ? 'Editar subevento' : 'Adicionar subevento'}</DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'hidden', minWidth: 0 }}>
          <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <TextField
              label="Nome do subevento"
              fullWidth
              required
              value={subEventForm.nome}
              onChange={(e) => setSubEventForm((prev) => ({ ...prev, nome: e.target.value }))}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              minRows={3}
              value={subEventForm.descricao}
              onChange={(e) => setSubEventForm((prev) => ({ ...prev, descricao: e.target.value }))}
            />
            <Grid container spacing={2} sx={{ width: '100%', minWidth: 0 }}>
              <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TextField
                  label="Início"
                  type="datetime-local"
                  fullWidth
                  sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0, maxWidth: '100%' } }}
                  value={subEventForm.start_datetime}
                  onChange={(e) => setSubEventForm((prev) => ({ ...prev, start_datetime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TextField
                  label="Término"
                  type="datetime-local"
                  fullWidth
                  sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0, maxWidth: '100%' } }}
                  value={subEventForm.end_datetime}
                  onChange={(e) => setSubEventForm((prev) => ({ ...prev, end_datetime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Limite de participantes"
              type="number"
              fullWidth
              inputProps={{ min: 1 }}
              value={subEventForm.participation_limit}
              onChange={(e) => setSubEventForm((prev) => ({ ...prev, participation_limit: e.target.value }))}
              placeholder="Deixe em branco para ilimitado"
            />
            <TextField
              label="Endereço ou referência"
              fullWidth
              value={subEventForm.endereco}
              onChange={(e) => setSubEventForm((prev) => ({ ...prev, endereco: e.target.value }))}
            />
            <MapRadiusPicker
              value={{
                latitude: subEventForm.latitude,
                longitude: subEventForm.longitude,
                radius_m: subEventForm.radius_m,
                endereco: subEventForm.endereco,
              }}
              onChange={(value) => setSubEventForm((prev) => ({ ...prev, ...value }))}
              mapHeight={320}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialogs} className="ghost-button">
            Cancelar
          </Button>
          <Button onClick={handleSubEventSubmit} variant="contained" className="primary-button">
            {editingSubEvent ? 'Salvar alterações' : 'Criar subevento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, message: '' })}
        PaperProps={{ className: 'dialog-surface' }}
      >
        <DialogTitle>Confirmar ação</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, message: '' })} className="ghost-button">
            Cancelar
          </Button>
          <Button onClick={executeConfirmAction} color="error" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsPage;
